import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { formatPrice, CATEGORY_LABELS, resolveImage } from "@/lib/format";
import { Plus, Edit2, Trash2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const load = () => api.get("/admin/products").then((r) => setProducts(r.data || []));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!confirm("Delete this product? Customers will no longer see it.")) return;
    try { await api.delete(`/admin/products/${id}`); toast.success("Product deleted"); load(); }
    catch { toast.error("Could not delete"); }
  };

  const toggleActive = async (p) => {
    try { await api.put(`/admin/products/${p.id}`, { is_active: !p.is_active }); load(); } catch {}
  };

  return (
    <div className="p-5 sm:p-8 lg:p-12" data-testid="admin-products-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-neutral-500">Catalog</div>
          <h1 className="editorial font-serif-display text-3xl sm:text-4xl mt-2">Products</h1>
        </div>
        <Link to="/admin/products/new" className="pill-btn h-11 px-5 rounded-full bg-black text-white text-sm hover:bg-neutral-800 inline-flex items-center gap-2" data-testid="new-product-btn">
          <Plus className="h-4 w-4" /> New product
        </Link>
      </div>

      {products.length === 0 ? (
        <div className="mt-8 border border-dashed border-black/15 rounded-sm px-5 py-16 text-center text-neutral-500 text-sm bg-white" data-testid="no-products">
          No products yet. <Link to="/admin/products/new" className="underline">Create your first one</Link>.
        </div>
      ) : (
        <>
          {/* Mobile / small screens: stacked cards */}
          <div className="mt-8 space-y-3 md:hidden">
            {products.map((p) => (
              <article key={p.id} className="bg-white border border-black/10 rounded-sm p-4" data-testid={`product-row-${p.id}`}>
                <div className="flex gap-3">
                  <div className="w-16 h-20 shrink-0 product-media">
                    {p.image_url ? <img src={resolveImage(p.image_url)} alt="" /> : <div className="h-full w-full ph-checker" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <div className="font-medium text-sm leading-snug break-words">{p.title}</div>
                      <div className="text-sm whitespace-nowrap">{formatPrice(p.price, p.currency)}</div>
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-neutral-500">
                      <span className="text-neutral-700">{CATEGORY_LABELS[p.category] || p.category}</span>
                      <span>·</span>
                      <span className={p.is_active ? "text-green-700" : "text-neutral-500"}>
                        {p.is_active ? "Active" : "Hidden"}
                      </span>
                      {p.featured && <><span>·</span><span>Featured</span></>}
                    </div>
                    {p.file_name && (
                      <div className="mt-1 text-[11px] text-neutral-500 truncate">File: {p.file_name}</div>
                    )}
                  </div>
                </div>
                <div className="mt-4 pt-3 border-t border-black/5 flex items-center justify-end gap-2">
                  <button
                    onClick={() => toggleActive(p)}
                    title={p.is_active ? "Hide" : "Show"}
                    className="h-9 w-9 inline-flex items-center justify-center rounded-full border border-black/10 hover:bg-neutral-100"
                    data-testid={`toggle-${p.id}`}
                  >
                    {p.is_active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                  </button>
                  <Link
                    to={`/admin/products/${p.id}/edit`}
                    className="h-9 w-9 inline-flex items-center justify-center rounded-full border border-black/10 hover:bg-neutral-100"
                    data-testid={`edit-${p.id}`}
                  >
                    <Edit2 className="h-4 w-4" />
                  </Link>
                  <button
                    onClick={() => remove(p.id)}
                    className="h-9 w-9 inline-flex items-center justify-center rounded-full border border-red-200 text-red-600 hover:bg-red-50"
                    data-testid={`delete-${p.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </article>
            ))}
          </div>

          {/* Desktop / md+: table (unchanged visual) */}
          <div className="mt-10 border border-black/10 bg-white rounded-sm overflow-hidden hidden md:block">
            <div className="grid grid-cols-12 px-5 py-3 text-xs uppercase tracking-widest text-neutral-500 border-b border-black/5">
              <div className="col-span-6">Product</div>
              <div className="col-span-2">Category</div>
              <div className="col-span-2">Price</div>
              <div className="col-span-2 text-right">Actions</div>
            </div>
            {products.map((p) => (
              <div key={p.id} className="grid grid-cols-12 items-center px-5 py-4 border-b border-black/5 last:border-b-0" data-testid={`product-row-desktop-${p.id}`}>
                <div className="col-span-6 flex items-center gap-3 min-w-0">
                  <div className="w-10 h-12 shrink-0 product-media">
                    {p.image_url ? <img src={resolveImage(p.image_url)} alt="" /> : <div className="h-full w-full ph-checker" />}
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{p.title}</div>
                    <div className="text-xs text-neutral-500 truncate">
                      {p.is_active ? "Active" : "Hidden"}{p.featured ? " · Featured" : ""}{p.file_name ? ` · ${p.file_name}` : ""}
                    </div>
                  </div>
                </div>
                <div className="col-span-2 text-sm text-neutral-700 truncate">{CATEGORY_LABELS[p.category] || p.category}</div>
                <div className="col-span-2 text-sm">{formatPrice(p.price, p.currency)}</div>
                <div className="col-span-2 flex items-center justify-end gap-1">
                  <button onClick={() => toggleActive(p)} title={p.is_active ? "Hide" : "Show"} className="h-9 w-9 inline-flex items-center justify-center rounded-full hover:bg-neutral-100" data-testid={`toggle-desktop-${p.id}`}>
                    {p.is_active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                  </button>
                  <Link to={`/admin/products/${p.id}/edit`} className="h-9 w-9 inline-flex items-center justify-center rounded-full hover:bg-neutral-100" data-testid={`edit-desktop-${p.id}`}>
                    <Edit2 className="h-4 w-4" />
                  </Link>
                  <button onClick={() => remove(p.id)} className="h-9 w-9 inline-flex items-center justify-center rounded-full hover:bg-red-50 text-red-600" data-testid={`delete-desktop-${p.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
